package gr.tech.site.pages.landings

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Background
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.functions.LinearGradient
import com.varabyte.kobweb.compose.css.functions.linearGradient
import com.varabyte.kobweb.compose.css.functions.toImage
import com.varabyte.kobweb.compose.css.objectFit
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.components.layouts.PageLayoutData
import gr.tech.site.components.sections.RelatedSection
import gr.tech.site.pages.SitePage
import gr.tech.site.styles.Theme
import gr.tech.site.styles.landings.*
import gr.tech.site.util.ImageRepo
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.rgba
import org.jetbrains.compose.web.dom.*

@InitRoute
fun initLEDRebatesPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(
        showContactHeader = true,
        title = "LED Grow Light Rebates - Up to 100% Back on DLC-Qualified Fixtures",
        description = "DLC-qualified LED grow lights can qualify for utility rebates covering up to 100% of equipment cost. Green Rebates identifies the right programs and handles every application for commercial cultivators.",
        canonicalUrl = "https://greenrebates.com/led-grow-light-rebates",
        ogImage = "/images/ledgrowlights.jpg"
    ))
}

@Page(SitePage.Landing.LED_GROW_LIGHT_REBATES)
@Layout(".components.layouts.PageLayout")
@Composable
fun LEDRebatesPage() {
    Main(
        attrs = Modifier
            .classNames("d-flex", "flex-column")
            .fillMaxSize()
            .role("main")
            .toAttrs()
    ) {

        // HERO
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(560.px)
                .position(Position.Relative)
                .classNames("px-0"),
            contentAlignment = Alignment.Center
        )
        {

            // Hero Image
            Img(
                src = ImageRepo.LEDGrowLights.src,
                alt = ImageRepo.LEDGrowLights.alt,
                attrs = Modifier.fillMaxSize().toAttrs{
                    attr("fetchpriority", "high") // ensures LCP optimization
                    style {
                        objectFit(ObjectFit.Cover) // preserve background-like cover
                    }
                }
            )

            Div(
                attrs = Modifier
                    .fillMaxSize()
                    .position(Position.Absolute)
                    .top(0.px).left(0.px)
                    .background(
                        Background.of(
                            image = linearGradient(
                                from = Theme.DarkGreen.rgb,
                                to = rgba(2, 67, 66, 0),
                                dir = LinearGradient.Direction.ToRight
                            ).toImage()
                        )
                    )
                    .toAttrs()
            )

            Column(
                modifier = LandingHeroStyle.toModifier().classNames("container", "text-start").fillMaxWidth(),
                horizontalAlignment = Alignment.Start
            )
            {
                H3(
                    attrs = LandingHeroEyebrowStyle.toModifier()
                        .classNames("text-uppercase", "mb-2").toAttrs()
                ) {
                    Text("LED Grow Light & Horticulture Lighting Rebates")
                }

                H1(
                    attrs = LandingHeroHeadlineStyle.toModifier()
                        .classNames("mb-3").toAttrs()
                ) {
                    Text("Get Paid to Switch to LED Grow Lights")
                }

                P(
                    attrs = LandingHeroLeadStyle.toModifier()
                        .classNames("mb-4").toAttrs()
                ) {
                    Text("Commercial growers are leaving thousands in unclaimed LED grow light rebates. Green Rebates secures up to 100% funding on DLC-qualified LED horticulture fixtures for indoor growers, cultivation facilities, and greenhouse operations.")
                }

                Link(
                    path = "/contact-us",
                    text = "Get Your Free LED Rebate Estimate \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaPrimaryStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Get a free LED grow light rebate estimate") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }

        // HOW IT WORKS
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(
                    attrs = LandingSectionHeadingStyle.toModifier()
                        .classNames("mb-2").toAttrs()
                ) {
                    Text("How Green Rebates Secures Your LED Grow Light Rebates")
                }
                P(
                    attrs = LandingSectionSubtitleStyle.toModifier()
                        .classNames("mb-5").toAttrs()
                ) {
                    Text("Three steps to maximize LED horticulture lighting rebates for your indoor grow or cultivation facility.")
                }

                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-md-3", "g-4").toAttrs()) {
                    listOf(
                        Triple("1", "You contact us, and we get you pre-approved!",
                            "LED grow light rebate applications require precise documentation and utility pre-approval. We gather what we need from you and handle the rest from day one."),
                        Triple("2", "We Handle the Paperwork & Help You Get Your Lights",
                            "Our specialists manage every form, submission, and follow-up with the utility. So you can stay focused on your grow operation."),
                        Triple("3", "You Collect Your Rebate Check",
                            "Once approved, your rebate check arrives with no chasing utilities, no follow-up calls. Just money back on the LED upgrade you were already planning.")
                    ).forEach { (num, title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(
                                attrs = Modifier
                                    .classNames("d-flex", "flex-column", "align-items-center", "text-center", "gap-3", "h-100", "py-4", "px-3")
                                    .toAttrs()
                            ) {
                                Div(
                                    attrs = LandingStepCardNumberStyle.toModifier()
                                        .classNames("d-flex", "align-items-center", "justify-content-center")
                                        .toAttrs()
                                ) { Text(num) }
                                H4(
                                    attrs = LandingStepCardTitleStyle.toModifier()
                                        .classNames("mb-1")
                                        .toAttrs()
                                ) { Text(title) }
                                P(
                                    attrs = LandingStepCardBodyStyle.toModifier()
                                        .classNames("mb-0")
                                        .toAttrs()
                                ) { Text(desc) }
                            }
                        }
                    }
                }
            }
        }

        // WHY NOW
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-7")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container", "text-center").toAttrs()) {
                H2(
                    attrs = LandingDarkSectionHeadingStyle.toModifier()
                        .classNames("mb-3")
                        .toAttrs()
                ) {
                    Text("Why Indoor Growers Can't Afford to Wait on LED Rebates")
                }
                P(
                    attrs = LandingDarkBodyStyle.toModifier()
                        .classNames("mb-4", "mx-auto")
                        .toAttrs()
                ) {
                    Text("Lighting is one of the largest energy costs in any indoor grow operation. LED grow light rebate programs are heavily in-demand — and utility program funding is limited. Every month you delay an LED retrofit is money permanently left on the table.")
                }
                Link(
                    path = "/contact-us",
                    text = "Check Your LED Grow Light Rebates Now \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaOutlineStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Check your LED grow light rebates now") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }

        // WHO CAN BENEFIT
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.LightGray.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                Div(attrs = Modifier.classNames("row", "align-items-center", "g-4").toAttrs()) {

                    // Left — image placeholder
                    Div(attrs = Modifier.classNames("col-lg-6", "order-lg-1", "order-2").toAttrs()) {
                        Img(
                            src = ImageRepo.CannabisPhoto.src,
                            alt = ImageRepo.CannabisPhoto.alt,
                            attrs = Modifier.classNames("img-fluid", "rounded-4", "shadow", "w-100")
                                .toAttrs { attr("loading", "lazy") }
                        )
                    }

                    // Right — copy + checklist + CTA
                    Div(
                        attrs = Modifier
                            .classNames("col-lg-6", "order-lg-2", "order-1", "text-lg-start", "text-center")
                            .toAttrs()
                    ) {
                        Div(
                            attrs = Modifier
                                .classNames("d-flex", "flex-column", "align-items-lg-start", "align-items-center", "gap-3")
                                .toAttrs()
                        ) {
                            H2(attrs = LandingSubSectionHeadingStyle.toModifier().toAttrs()) {
                                Text("Who Qualifies for LED Grow Light Rebates")
                            }

                            P(attrs = LandingMutedBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                Text("Whether you operate an indoor cultivation facility, a commercial greenhouse, or a large-scale grow operation — Green Rebates finds every available LED horticulture rebate for your facility.")
                            }

                            Ul(
                                attrs = Modifier
                                    .classNames("list-unstyled", "d-flex", "flex-column", "gap-2", "text-start", "w-100", "mb-0")
                                    .toAttrs()
                            ) {
                                listOf(
                                    "Indoor grow facilities switching from HID or HPS to LED lighting",
                                    "Greenhouse and horticulture operations upgrading to DLC-qualified fixtures",
                                    "Commercial growers replacing legacy lighting with high-efficiency LEDs"
                                ).forEach { item ->
                                    Li(attrs = Modifier.classNames("d-flex", "align-items-center", "gap-2").toAttrs()) {
                                        FaCheck(
                                            modifier = LandingCheckmarkStyle.toModifier().classNames("flex-shrink-0"),
                                            size = IconSize.SM
                                        )
                                        Span { Text(item) }
                                    }
                                }
                            }

                            Link(
                                path = "/contact-us",
                                text = "Find Out If Your Facility Qualifies \u2192",
                                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                                modifier = LandingCtaSecondaryStyle.toModifier()
                                    .classNames("mt-2")
                                    .role("button")
                                    .attrsModifier { attr("aria-label", "Find out if your grow facility qualifies for LED grow light rebates") },
                                openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                                openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                            )
                        }
                    }
                }
            }
        }

        // WHAT REBATES ARE AVAILABLE
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(
                    attrs = LandingSectionHeadingStyle.toModifier()
                        .classNames("mb-2")
                        .toAttrs()
                ) {
                    Text("Types of LED Grow Light Rebates Available")
                }
                P(
                    attrs = LandingSectionSubtitleStyle.toModifier()
                        .classNames("mb-5")
                        .toAttrs()
                ) {
                    Text("Utility-backed LED lighting incentives for indoor growers, horticulture facilities, and commercial cultivation operations.")
                }

                Div(
                    attrs = Modifier
                        .classNames("row", "row-cols-1", "row-cols-sm-2", "row-cols-lg-4", "g-4")
                        .toAttrs()
                ) {
                    listOf(
                        "LED Horticulture Lighting Rebates" to
                            "Replace HID, HPS, or CMH fixtures with DLC-qualified LED grow lights and recover a significant share of your upgrade costs through utility rebate programs.",
                        "LED Retrofit Rebates" to
                            "Already have a facility? LED retrofit rebates cover the cost of converting existing fixtures to high-efficiency LED grow lights — no new build required.",
                        "DLC-Qualified Fixture Incentives" to
                            "Most utility rebate programs require DLC-qualified products. Green Rebates guides you toward eligible fixtures to maximize your rebate before you buy.",
                        "Indoor Grow & Greenhouse Programs" to
                            "Targeted LED energy efficiency programs for indoor cultivation facilities and commercial greenhouse operations of all sizes."
                    ).forEachIndexed { index, (title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = LandingRebateCardStyle.toModifier().toAttrs()) {
                                when (index) {
                                    0 -> FaLightbulb(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                    1 -> FaFan(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                    2 -> FaDroplet(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                    else -> FaSeedling(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                }
                                // No explicit color — inherits white from LandingRebateCardStyle on hover
                                H5(attrs = LandingRebateCardTitleStyle.toModifier().classNames("mb-2").toAttrs()) {
                                    Text(title)
                                }
                                P(attrs = LandingRebateCardBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                    Text(desc)
                                }
                            }
                        }
                    }
                }
            }
        }

        // WHY GREEN REBATES
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(
                    attrs = LandingDarkSectionHeadingStyle.toModifier()
                        .classNames("mb-5")
                        .toAttrs()
                ) {
                    Text("Why Growers Choose Green Rebates for LED Rebates")
                }

                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-sm-2", "g-4").toAttrs()) {
                    listOf(
                        "LED Rebate Expertise" to
                            "We specialize exclusively in LED grow light and horticulture lighting rebates — not general energy consulting. We know every DLC-qualified incentive program available to indoor growers.",
                        "Direct Utility Relationships" to
                            "Our relationships with utilities and program administrators mean faster pre-approvals and higher LED rebate payouts for your grow facility.",
                        "Full-Service LED Retrofit Support" to
                            "From grow room lighting audits to rebate checks — we manage every step of the LED upgrade process for your indoor grow or cultivation operation.",
                        "Maximize Every Fixture" to
                            "We research every LED grow light rebate and energy efficiency incentive available — including stacking opportunities — to get your facility the maximum possible funding."
                    ).forEach { (title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = Modifier.classNames("d-flex", "gap-3", "align-items-start").toAttrs()) {
                                Div(
                                    attrs = LandingCheckIconStyle.toModifier()
                                        .classNames("d-flex", "align-items-center", "justify-content-center", "flex-shrink-0")
                                        .toAttrs()
                                ) { FaCheck(size = IconSize.SM) }
                                Div {
                                    H5(attrs = LandingDarkItemTitleStyle.toModifier().classNames("mb-1").toAttrs()) {
                                        Text(title)
                                    }
                                    P(attrs = LandingDarkItemBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                        Text(desc)
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // TESTIMONIALS
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.LightGray.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(
                    attrs = LandingSectionHeadingStyle.toModifier()
                        .classNames("mb-5")
                        .toAttrs()
                ) {
                    Text("What Growers Are Saying About LED Rebates")
                }

                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-md-2", "g-4").toAttrs()) {
                    listOf(
                        "Green Rebates got our facility over $50,000 back on LED grow light and HVAC upgrades. The entire process was completely effortless — they handled everything from start to finish." to
                            "Licensed Cultivator - California",
                        "I didn't think utility rebates could cover this much of our LED lighting costs. Green Rebates managed every application from start to finish. I'd recommend them to any commercial grower." to
                            "Cultivation Facility Manager - Oregon"
                    ).forEach { (quote, attribution) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = LandingTestimonialCardStyle.toModifier().toAttrs()) {
                                Span(
                                    attrs = LandingQuoteMarkStyle.toModifier()
                                        .classNames("d-block", "mb-1")
                                        .toAttrs()
                                ) { Text("\u201C") }
                                P(attrs = LandingBodyStyle.toModifier().classNames("fst-italic", "mb-3").toAttrs()) {
                                    Text(quote)
                                }
                                Span(attrs = LandingAttributionStyle.toModifier().toAttrs()) {
                                    Text("- $attribution")
                                }
                            }
                        }
                    }
                }
            }
        }

        RelatedSection(listOf(
            "/article/top-10-high-efficiency-led-rebates-for-commercial-cultivators-in-2026" to "Top 10 High-Efficiency LED Rebates for Commercial Cultivators in 2026",
            "/article/energy-savings-great-financial-savings-even-better-benefits-switching-led-grow-lights" to "Energy Savings? Great. Financial Savings? Even Better: The Benefits of Switching to LED Grow Lights",
            "/article/brightening-the-future-of-agriculture-the-role-of-led-lighting-in-reap" to "Brightening the Future of Agriculture: The Role of LED Lighting in REAP",
        ))

        // FINAL CTA
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .id("contact")
                .backgroundColor(Theme.MediumGreen.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container", "text-center").toAttrs()) {
                H2(
                    attrs = LandingCtaHeadingStyle.toModifier()
                        .classNames("mb-3")
                        .toAttrs()
                ) {
                    Text("Ready to Claim Your LED Grow Light Rebates?")
                }
                P(
                    attrs = LandingCtaBodyStyle.toModifier()
                        .classNames("mb-4", "mx-auto")
                        .toAttrs()
                ) {
                    Text("Don't let your grow facility miss out on LED horticulture rebates, DLC-qualified incentives, and energy efficiency programs. Green Rebates maximizes LED lighting rebates for indoor growers, cultivation facilities, and greenhouse operations. Get started today.")
                }
                Link(
                    path = "/contact-us",
                    text = "Contact Us for a Free LED Rebate Estimate \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaThirdStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Contact Green Rebates for a free LED grow light rebate estimate") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }
    }
}
